import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const TermsAndConditions = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [hasReadTerms, setHasReadTerms] = useState(false);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const sections = [
    {
      id: "eligibility",
      title: "1. User Eligibility",
      content: [
        {
          items: [
            "You must be at least 18 years old to use our services. By using TezChal, you confirm that you meet this requirement.",
            "If you are using TezChal on behalf of a business, you warrant that you have the authority to bind that business to these Terms.",
          ],
        },
      ],
    },
    {
      id: "registration",
      title: "2. Account Registration",
      content: [
        {
          items: [
            "You must create an account to place orders on TezChal.",
            "You are responsible for maintaining the confidentiality of your account credentials.",
            "We reserve the right to suspend or terminate accounts that violate our policies or engage in fraudulent activities.",
          ],
        },
      ],
    },
    {
      id: "payments",
      title: "3. Ordering and Payments",
      content: [
        {
          items: [
            "Orders placed on TezChal are final and cannot be canceled once confirmed.",
            "We accept multiple payment methods. Payments are securely processed through third-party payment gateways.",
            "If payment fails, your order may be canceled or delayed.",
          ],
        },
      ],
    },
    {
      id: "delivery",
      title: "4. Delivery Policy",
      content: [
        {
          items: [
            "We strive to deliver orders within the estimated time frame, but delays may occur due to unforeseen circumstances.",
            "You must provide accurate delivery details to ensure timely and successful delivery.",
            "If you fail to accept the order at the provided address, additional delivery charges may apply.",
          ],
        },
      ],
    },
    {
      id: "returns",
      title: "5. Returns and Refunds",
      content: [
        {
          items: [
            "Returns and refunds are subject to our Return Policy, available in the app.",
            "If you receive a damaged or incorrect item, you must report it within [X] hours for resolution.",
            "Refunds (if applicable) will be processed based on the original payment method.",
          ],
        },
      ],
    },
    {
      id: "conduct",
      title: "6. User Conduct",
      content: [
        {
          items: [
            "You agree not to misuse our services or engage in unlawful activities.",
            "You must not attempt to hack, disrupt, or reverse-engineer any part of our platform.",
            "We reserve the right to restrict access to users who violate these Terms.",
          ],
        },
      ],
    },
    {
      id: "intellectual-property",
      title: "7. Intellectual Property",
      content: [
        {
          items: [
            "All content, logos, and trademarks on TezChal are the property of TezChal Private Limited.",
            "You may not reproduce, distribute, or modify any part of our platform without our consent.",
          ],
        },
      ],
    },
    {
      id: "liability",
      title: "8. Limitation of Liability",
      content: [
        {
          items: [
            "We are not responsible for indirect, incidental, or consequential damages arising from your use of TezChal.",
            "Our total liability shall not exceed the amount you paid for the services provided.",
          ],
        },
      ],
    },
    {
      id: "modifications",
      title: "9. Modifications to Terms",
      content: [
        {
          items: [
            "We may update these Terms from time to time. Continued use of our services after changes constitutes acceptance of the revised Terms.",
          ],
        },
      ],
    },
    {
      id: "governing-law",
      title: "10. Governing Law",
      content: [
        {
          items: [
            "These Terms shall be governed by and interpreted under the laws of Karnataka.",
            "Any disputes shall be resolved in the courts of Karnataka.",
          ],
        },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header with decorative elements */}
        <div className="text-center mb-8 relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
          <h1 className="lg:text-3xl xsm:text-xl font-bold mb-2 pt-4">Terms and Conditions</h1>
          {/* <p className="text-gray-600">Effective Date: [Insert Date]</p> */}
        </div>

        {/* Welcome Banner */}
        <div className="bg-white rounded-lg shadow-md mb-6 border-l-4 border-blue-500">
          <div className="p-6">
            <p className="text-lg">
              Welcome to <span className="font-bold">TezChal</span>! These Terms
              and Conditions ("Terms") govern your use of the{" "}
              <span className="font-bold">TezChal</span> quick commerce mobile
              application and services provided by{" "}
              <span className="font-bold">TezChal Private Limited</span>{" "}
              ("TezChal," "we," "our," or "us"). By accessing or using our
              services, you agree to these Terms.
            </p>
          </div>
        </div>

        {/* Collapsible Sections */}
        {sections.map((section) => (
          <div
            key={section.id}
            className="bg-white rounded-lg shadow-md mb-4 transition-all duration-200 hover:shadow-lg"
          >
            <div
              className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-t-lg"
              onClick={() => toggleSection(section.id)}
            >
              <h2 className="lg:text-xl xsm:text-[16.5px] font-semibold">{section.title}</h2>
              {expandedSections[section.id] ? (
                <ChevronUp className="w-6 h-6 text-gray-500" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-500" />
              )}
            </div>

            {expandedSections[section.id] && (
              <div className="p-4 pt-0 border-t border-gray-100">
                {section.content.map((contentBlock, idx) => (
                  <div key={idx} className="mb-4">
                    <ul className="space-y-3">
                      {contentBlock.items.map((item, itemIdx) => (
                        <li
                          key={itemIdx}
                          className="text-gray-700 flex items-start"
                        >
                          <span className="mr-2 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-md mt-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
            <p className="mb-4">
              For any questions or concerns regarding these Terms, contact us
              at:
            </p>
            <div className="space-y-2">
              <p className="font-bold">TezChal Private Limited</p>
              <p>
                Apartment Sandeep Vihar, Block/Tower G-25, <br/>Floor No. 13 and 14,
                Kannamangala Village <br/>Bidarahalli, Bengaluru East Taluk,
                Karnataka-560067
              </p>
              <p>info@tezchal.com</p>
              <p>+91 8981440034</p>
            </div>
          </div>
        </div>

        {/* Agreement Section */}
        {/* <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <div className="flex items-center space-x-3 mb-4">
            <input
              type="checkbox"
              id="agree-terms"
              className="w-4 h-4 text-blue-600"
              checked={hasReadTerms}
              onChange={(e) => setHasReadTerms(e.target.checked)}
            />
            <label htmlFor="agree-terms" className="text-gray-700">
              I have read and agree to the Terms and Conditions
            </label>
          </div>
          <button
            className={`w-full py-3 rounded-lg font-semibold ${
              hasReadTerms
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            disabled={!hasReadTerms}
          >
            Accept Terms
          </button>
        </div> */}
      </div>
      <Footer />
    </>
  );
};

export default TermsAndConditions;